/* eslint-disable react/no-danger */
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate, Location } from 'react-router-dom'
import { Box, Button, Flex, Heading, Skeleton, Stack, Text } from '@chakra-ui/react'

import { useOfferingProvider } from 'providers/offeringProvider/OfferingProvider'
import { useGlobalContext } from 'providers/GlobalProvider'
import FooterButtons from 'components/common/FooterButtons/FooterButtons'
import SummaryPlan from 'components/offering/Summary/SummaryPlan'
import ResumeBankAccordion from 'components/ResumeBankAccordion/ResumeBankAccordion'
import { pluralize } from 'utils/functions/formatString'
import { isSuccessStatus } from 'utils/functions/validations'
import { getResumen } from 'services/api'
import { IAccountProvider, IAdditionalService } from 'providers/offeringProvider/IOfferingProvider.d'
import { IFetchedGetResume, IOfferingsB } from 'services/IApi.d'

const ResumePage = () => {
  interface RouteState {
    pageBefore?: string
    edit?: boolean
  }
  const navigate = useNavigate()
  const location: Location = useLocation()
  const { state }: { state?: RouteState } = location
  const { pageBefore, edit: resumeEdit }: RouteState = state || {}
  const isBeforeAdditionalServices = pageBefore === 'additionalServices'
  const { idEmpresa, idSuscriptor } = useGlobalContext()
  const { selectedOfferings, offerings, accounts, setSelectedOfferings, setChangePlan } = useOfferingProvider()
  const [accountSelected, setAccountSelected] = useState<IAccountProvider[]>()
  const [loadingNext, setLoadingNext] = useState<boolean>(false)

  const title = '¡Felicitaciones! Elegiste tu plan y cuentas'
  const numberBanksSelected = accountSelected?.length
  const numberAccountSelected = accountSelected?.reduce((prev, curr) => prev + curr.accounts.length, 0)
  const subtitle = `Vas a usar Interbanking con <b>${numberBanksSelected} ${pluralize(numberBanksSelected, 'banco', 's')}</b> y <b>${numberAccountSelected} ${pluralize(numberAccountSelected, 'cuenta', 's')}</b>`
  const primaryText = 'Ir al inicio'
  const secondaryText = 'Volver'
  const accountsPath = '/cuentas'

  const handleBack = () => navigate('/servicios-adicionales')
  const handleContinue = async () => {
    setLoadingNext(true)
    navigate('/dashboard')
  }
  const handleNavigate = (path: string) => {
    navigate(path)
  }

  const getSelectedAccounts = accountsArray => accounts?.filter(bank => accountsArray?.find(filtro => filtro.bankId === bank.bankId)).map(bank => {
    const bankFilter = accountsArray?.find(filtro => filtro.bankId === bank.bankId)
    const accountFilter = bank.accounts.filter(cuenta => bankFilter.accounts.some(filtroCuenta => filtroCuenta.accountId === cuenta.accountId))
    return {
      ...bank,
      accounts: accountFilter,
    }
  })

  const getOffering = offeringArray => offerings?.find(offering => offering.codeDn === offeringArray.codeDn)

  const getResumenDataInitial = async () => {
    const response: IFetchedGetResume = await getResumen(idSuscriptor, idEmpresa)

    if (!isSuccessStatus(response.status)) {
      navigate('/error')
      return null
    }
    return response?.data
  }

  useEffect(() => {
    setChangePlan(false)
    if (isBeforeAdditionalServices) {
      const AccountSelectedProvider = getSelectedAccounts(selectedOfferings.accounts)
      setAccountSelected(AccountSelectedProvider)
    }

    // se ejecuta cuando pongan el botón Ver desde el dashboard
    if (!isBeforeAdditionalServices && accounts) {
      getResumenDataInitial()
        .then(result => {
          if (!result) return
          const { plan, additionalServices, banks } = result
          const AccountSelected: IAccountProvider[] = getSelectedAccounts(banks)
          setAccountSelected(AccountSelected)
          const offeringSelected: IOfferingsB = getOffering(plan)
          const additionalServicesMapped: IAdditionalService[] = additionalServices?.map(as => ({ ...as, description: '' }))
          setSelectedOfferings(old => ({ ...old, accounts: AccountSelected, plan: offeringSelected, additionalServices: additionalServicesMapped }))
        })
    }
  }, [idSuscriptor, idEmpresa, accounts])

  return (
    <div>
      <Box>
        <Box textStyle='h1' mb={2} data-testid='title'>{title}</Box>
        <Flex>
          <Box flex={0.66} pr={4}>
            {!accountSelected &&
              <Stack>
                <Skeleton height='100px' />
                <Skeleton height='100px' />
                <Skeleton height='100px' />
              </Stack>
            }
            {accountSelected?.length > 0 &&
              <Box>
                <Flex justifyContent='space-between' my='8'>
                  <Text textStyle='h5' mb={4}>
                    <Box data-testid='subtitle' dangerouslySetInnerHTML={{ __html: subtitle }} />
                  </Text>
                  <Button 
                    onClick={() => handleNavigate(accountsPath)} 
                    p={4} variant='primary-outline' 
                    color='primary.500'
                    data-testid='changeAccounts-button'
                    disabled={!resumeEdit }
                    >
                    Cambiar
                  </Button>
                </Flex>
                <Box height='45vh' overflow='auto'>
                  {accountSelected?.map(bank => (
                    <ResumeBankAccordion isPhysicalSubscription={!bank.acceptDigitalSubscription} name={bank.name} accounts={bank?.accounts} />
                  ))}
                </Box>
              </Box>
            }
          </Box>
          {accountSelected && (
            <Box flex={0.34}>
              <SummaryPlan
                step={ resumeEdit || isBeforeAdditionalServices ? 'summary' : 'summaryFinal'}
              />
            </Box>
          )}
        </Flex>
        <FooterButtons
          onBack={handleBack}
          onNext={handleContinue}
          loading={loadingNext}
          backText={secondaryText}
          nextText={primaryText}
          showButtonBack={!resumeEdit}
        />
      </Box>
    </div>
  )
}

export default ResumePage
